/**
 * Manufactures Dual-Write Test
 *
 * Tests that manufacture operations in Firestore are properly synced to Supabase.
 *
 * Test variations:
 * M1: Create manufacture (composite product)
 * M2: Delete manufacture
 */

import {
  saveNewManufacture,
  deleteManufacture,
} from '../global/Firebase/firebase';
import { api } from '../global/API/apiClient';
import { getDefaultStore } from 'jotai';
import { organizationAtom, storesDictAtom, productsDictAtom, dbAtom } from '../global/AtomsProvider';
import { getDailyStatID } from './timezoneFunctions';

// Test variation definitions
export const MANUFACTURE_TEST_VARIATIONS = [
  { id: 'M1', category: 'create', name: 'Create manufacture' },
  { id: 'M2', category: 'delete', name: 'Delete manufacture' },
];

// Get test categories for UI grouping
export function getManufactureTestCategories() {
  return [
    { id: 'create', name: 'Create Manufacture', variations: ['M1'] },
    { id: 'delete', name: 'Delete Manufacture', variations: ['M2'] },
  ];
}

// Helper to normalize values for comparison
function normalizeValue(val) {
  if (val === undefined || val === null) return null;
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const num = parseFloat(val);
    if (!isNaN(num)) return num;
    return val.trim();
  }
  return val;
}

// Helper to compare values with tolerance for floating point
function valuesMatch(a, b, tolerance = 0.01) {
  const normA = normalizeValue(a);
  const normB = normalizeValue(b);

  if (normA === normB) return true;
  if (normA === null || normB === null) return normA === normB;

  // Numeric comparison with tolerance
  if (typeof normA === 'number' && typeof normB === 'number') {
    return Math.abs(normA - normB) < tolerance;
  }

  return normA === normB;
}

/**
 * Get test data based on atoms
 */
export function getManufactureTestData() {
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const storesDict = jotaiStore.get(storesDictAtom);
  const productsDict = jotaiStore.get(productsDictAtom);
  const db = jotaiStore.get(dbAtom);

  if (!organization || !db) {
    return null;
  }

  // Get first store
  const storeEntries = Object.entries(storesDict || {});
  if (storeEntries.length < 1) {
    return null;
  }

  const store = { id: storeEntries[0][0], ...storeEntries[0][1] };

  // Find a composite product for manufacture test
  const productEntries = Object.entries(productsDict || {});
  let compositeProduct = null;
  let ingredientProducts = [];

  for (const [id, product] of productEntries) {
    if (product.isComposite && product.ingredients?.length > 0) {
      compositeProduct = {
        id,
        ...product,
        ref: db.collection('products').doc(id),
      };
      // Get ingredient products
      for (const ing of product.ingredients) {
        const ingProduct = productsDict[ing.id];
        if (ingProduct) {
          ingredientProducts.push({
            id: ing.id,
            ...ingProduct,
            quantity: ing.quantity,
            ref: db.collection('products').doc(ing.id),
          });
        }
      }
      break;
    }
  }

  // If no composite product, create mock data (tests will skip)
  if (!compositeProduct) {
    return null;
  }

  return {
    organization,
    store,
    db,
    organizationId: organization.id,
    compositeProduct,
    ingredientProducts,
  };
}

/**
 * Compare manufacture via backend API
 */
async function compareManufactureViaApi(manufactureId, manufactureData, log) {
  try {
    const response = await api.get(`/manufactures/compare/${manufactureId}`);
    const apiResult = response.data;

    if (!apiResult.exists) {
      return {
        manufactureId,
        foundInSupabase: false,
        matches: false,
        differences: [{
          field: 'manufacture',
          supabaseValue: null,
          firestoreValue: 'exists',
          severity: 'critical',
        }],
        warnings: [],
      };
    }

    const supabaseData = apiResult.supabaseData;
    const differences = [];

    // Compare key fields
    const fieldsToCompare = [
      { fs: 'quantity', sb: 'quantity' },
      { fs: 'totalSum', sb: 'total_sum' },
    ];

    for (const { fs, sb } of fieldsToCompare) {
      const fsVal = normalizeValue(manufactureData?.[fs]);
      const sbVal = normalizeValue(supabaseData?.[sb]);

      if (!valuesMatch(fsVal, sbVal)) {
        differences.push({
          field: sb,
          supabaseValue: sbVal,
          firestoreValue: fsVal,
          severity: 'critical',
        });
      }
    }

    // Compare ingredients count
    const fsIngredientsCount = manufactureData?.ingredients?.length || 0;
    const sbIngredients = supabaseData?.ingredients;
    const sbIngredientsCount = Array.isArray(sbIngredients) ? sbIngredients.length : 0;

    if (fsIngredientsCount !== sbIngredientsCount) {
      differences.push({
        field: 'ingredients_count',
        supabaseValue: sbIngredientsCount,
        firestoreValue: fsIngredientsCount,
        severity: 'warning',
      });
    }

    return {
      manufactureId,
      foundInSupabase: true,
      matches: differences.filter(d => d.severity === 'critical').length === 0,
      differences,
      warnings: differences.filter(d => d.severity === 'warning').map(d => d.field),
      supabaseData,
    };
  } catch (error) {
    log(`API comparison error: ${error.message}`);
    return {
      manufactureId,
      foundInSupabase: false,
      matches: false,
      differences: [{
        field: 'api_error',
        supabaseValue: null,
        firestoreValue: error.message,
        severity: 'critical',
      }],
      warnings: [`API error: ${error.message}`],
    };
  }
}

/**
 * Check if manufacture was deleted from Supabase
 */
async function checkManufactureDeleted(manufactureId, maxRetries = 3) {
  for (let i = 0; i < maxRetries; i++) {
    try {
      const response = await api.get(`/manufactures/compare/${manufactureId}`);
      if (!response.data.exists) {
        return true;
      }
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 2000));
    } catch (error) {
      // 404 means deleted
      if (error.response?.status === 404) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Run a single manufacture test
 */
async function runSingleTest(variationId, testData, log) {
  const { db, store, organizationId, compositeProduct, ingredientProducts } = testData;

  let createdRef = null;
  let createdManufactureData = null;
  let result = {
    variationId,
    passed: false,
    error: null,
    comparisonResult: null,
  };

  try {
    switch (variationId) {
      case 'M1': {
        // Create manufacture
        const manufactureDate = new Date();

        const manufactureData = {
          product: compositeProduct,
          quantity: 1,
          ingredientsEdited: false,
          ingredients: ingredientProducts,
          totalSum: compositeProduct.purchasePrice || 100,
          date: manufactureDate,
          dailyStatID: getDailyStatID(null, manufactureDate) + '-' + store.id,
          store: store.id,
          organizationId,
        };

        log(`Creating manufacture for: "${compositeProduct.name}"`);
        createdRef = await saveNewManufacture(db, manufactureData);
        createdManufactureData = { ...manufactureData, id: createdRef.id };

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Compare
        const comparison = await compareManufactureViaApi(createdRef.id, manufactureData, log);
        result.comparisonResult = comparison;
        result.passed = comparison.foundInSupabase;

        // Cleanup
        log('Cleaning up test manufacture...');
        await deleteManufacture(db, createdRef, createdManufactureData);
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'M2': {
        // Delete manufacture test
        const manufactureDate = new Date();

        const manufactureData = {
          product: compositeProduct,
          quantity: 1,
          ingredientsEdited: false,
          ingredients: ingredientProducts,
          totalSum: compositeProduct.purchasePrice || 100,
          date: manufactureDate,
          dailyStatID: getDailyStatID(null, manufactureDate) + '-' + store.id,
          store: store.id,
          organizationId,
        };

        log('Creating manufacture to delete...');
        createdRef = await saveNewManufacture(db, manufactureData);
        createdManufactureData = { ...manufactureData, id: createdRef.id, ref: createdRef };

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Verify it exists first
        const createCheck = await compareManufactureViaApi(createdRef.id, manufactureData, log);
        if (!createCheck.foundInSupabase) {
          result.error = 'Manufacture was not created in Supabase';
          break;
        }

        // Now delete
        log('Deleting manufacture...');
        await deleteManufacture(db, createdRef, createdManufactureData);

        log('Waiting 6000ms for delete sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Check if deleted
        const deleted = await checkManufactureDeleted(createdRef.id);
        result.passed = deleted;
        result.comparisonResult = {
          manufactureId: createdRef.id,
          foundInSupabase: !deleted,
          matches: deleted,
          differences: deleted ? [] : [{
            field: 'deleted',
            supabaseValue: 'still exists',
            firestoreValue: 'deleted',
            severity: 'critical',
          }],
        };

        break;
      }

      default:
        result.error = `Unknown variation: ${variationId}`;
    }
  } catch (error) {
    result.error = error.message;
    log(`Error: ${error.message}`);

    // Cleanup on error
    if (createdRef && createdManufactureData) {
      try {
        await deleteManufacture(db, createdRef, createdManufactureData);
      } catch (cleanupError) {
        log(`Cleanup failed: ${cleanupError.message}`);
      }
    }
  }

  return result;
}

/**
 * Run all manufacture tests
 */
export async function runFullManufactureTest(log = console.log, selectedVariations = null) {
  log('Starting manufactures dual-write tests...');

  const testData = getManufactureTestData();
  if (!testData) {
    log('ERROR: Could not get test data. Need at least 1 store and a composite product.');
    return { passed: 0, failed: 0, skipped: 0, results: [] };
  }

  log('\n========================================');
  log('MANUFACTURES DUAL-WRITE TEST SUITE');
  log('========================================');
  log(`Organization: ${testData.organization.name || testData.organizationId}`);
  log(`Store: ${testData.store.name || testData.store.id}`);
  log(`Composite Product: ${testData.compositeProduct.name}`);
  log(`Ingredients: ${testData.ingredientProducts.map(p => p.name).join(', ')}`);
  log('========================================\n');

  const variationsToRun = selectedVariations || MANUFACTURE_TEST_VARIATIONS.map(v => v.id);
  const results = [];
  let passed = 0;
  let failed = 0;
  let skipped = 0;

  for (const variationId of variationsToRun) {
    const variation = MANUFACTURE_TEST_VARIATIONS.find(v => v.id === variationId);
    if (!variation) {
      log(`Skipping unknown variation: ${variationId}`);
      skipped++;
      continue;
    }

    log(`\n--- Running test ${variationId}: ${variation.name} ---`);

    const result = await runSingleTest(variationId, testData, log);
    results.push(result);

    if (result.error) {
      log(`ERROR: ${result.error}`);
      failed++;
    } else if (result.passed) {
      log(`PASS: ${variation.name}`);
      passed++;
    } else {
      log(`FAIL: ${variation.name}`);
      if (result.comparisonResult?.differences?.length > 0) {
        log('Differences: ' + JSON.stringify(result.comparisonResult.differences, null, 2));
      }
      failed++;
    }
  }

  log('\n========================================');
  log('TEST SUMMARY');
  log('========================================');
  log(`Passed: ${passed}`);
  log(`Failed: ${failed}`);
  log(`Skipped: ${skipped}`);
  log('========================================');

  if (failed > 0) {
    log('\nFAILED TESTS:');
    results.filter(r => !r.passed && !r.error).forEach(r => {
      const variation = MANUFACTURE_TEST_VARIATIONS.find(v => v.id === r.variationId);
      log(`  - ${r.variationId}: ${variation?.name}`);
      r.comparisonResult?.differences?.forEach(d => {
        log(`    ${d.field}: Supabase="${d.supabaseValue}" vs Firestore="${d.firestoreValue}"`);
      });
    });
  }

  return { passed, failed, skipped, results };
}

export default {
  MANUFACTURE_TEST_VARIATIONS,
  getManufactureTestCategories,
  getManufactureTestData,
  runFullManufactureTest,
};
